import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Box, ChevronLeft, ChevronRight, HardDrive, Inbox, Search, Server, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { CopyableId } from '@/components/CopyableId';
import { DetonationTag } from '@/components/DetonationTag';
import { IntakeChannelTag } from '@/components/IntakeChannelTag';
import OrgPicker, { ALL_ORGS, useActiveOrg } from '@/components/OrgPicker';
import { hasOSGlyph, OSIcon } from '@/components/OSIcon';
import { StatusBadge } from '@/components/StatusBadge';
import { getFileIcon } from '@/components/SubmissionIcon';
import {
  type Column,
  EmptyState,
  ErrorState,
  ResponsiveTable,
  Select,
  type SelectOption,
} from '@/components/ui';
import { VerdictBadge } from '@/components/VerdictBadge';
import {
  ACTIVE_STATUSES,
  api,
  osFamilyLabel,
  SUBMISSION_PAGE_SIZE,
  type Submission,
} from '@/lib/api';
import {
  formatRelativeTime,
  formatSubmittedSize,
  formatTimeoutShort,
  formatWallDuration,
} from '@/lib/format';
import { isContainerLane } from '@/lib/lanes';
import { TASK_STREAM_KINDS, useSSE } from '@/lib/sse';

/** The page sizes a reader can pick from. The server clamps a request to 200
 *  and holds the rest of the list behind the pager, so the widest page is a
 *  choice about how much to read at once, never about how much is reachable. */
const PER_PAGE_OPTIONS: ReadonlyArray<SelectOption> = [25, 50, 100, 200].map((n) => ({
  value: String(n),
  label: `${n} per page`,
}));

export default function SubmissionsPage() {
  const { orgId } = useActiveOrg();
  const qc = useQueryClient();
  const loc = useLocation();
  const nav = useNavigate();
  const [search, setSearch] = useState(() => {
    const q = (loc.state as { q?: string } | null)?.q;
    return typeof q === 'string' ? q : '';
  });
  const isAllOrgs = orgId === ALL_ORGS;

  // A full 64-char hex string is a SHA-256 lookup: the backend matches it
  // against the submitted sample AND any dropped-file artifact (which the
  // client rows don't carry), so it must run server-side. Anything shorter
  // stays a client-side filter over the current page.
  const fileHash = /^[0-9a-f]{64}$/i.test(search.trim()) ? search.trim().toLowerCase() : undefined;

  // The window of the list this page shows: which page, and how big. The
  // server holds every submission and is asked for one window, so a history
  // longer than a page is paged through rather than truncated at the newest
  // 50 rows — nothing in the list is unreachable.
  //
  // The offset is stored WITH the scope it belongs to. Resetting it in an
  // effect would be a render too late: the query starts on the render that
  // changed the scope, so it would first request the old offset under the new
  // scope (a wasted fetch, and a page that does not exist there) and then
  // page 1. Storing the pair means a scope change asks for page 1 immediately.
  // `search` is in the scope because a new search starts from the newest page,
  // not from wherever the reader was paging.
  const [perPage, setPerPage] = useState(SUBMISSION_PAGE_SIZE);
  const scope = `${orgId ?? ''}|${fileHash ?? ''}|${perPage}|${search}`;
  const [paging, setPaging] = useState({ scope, offset: 0 });
  // Adjust during render, not in an effect: the query already starts on the
  // render that changed the scope, so an effect would be a render too late.
  //
  // The reset must also be PERSISTED. Merely deriving 0 from a mismatch is a
  // mask, not a reset: returning to the old scope (a search typed and then
  // cleared, org A→B→A, page size 50→100→50) would match the stale pair again
  // and resurrect the old offset. This is React's guarded render-phase
  // adjustment; the guard is what keeps it from looping.
  if (paging.scope !== scope) {
    setPaging({ scope, offset: 0 });
  }
  const offset = paging.scope === scope ? paging.offset : 0;

  const subsQ = useQuery({
    // The page is part of the key, so each window caches on its own and SSE
    // invalidation still covers every window through the key prefix.
    queryKey: ['submissions', orgId, fileHash ?? null, perPage, offset],
    queryFn: () => api.listSubmissions(orgId!, { fileHash, limit: perPage, offset }),
    // Keep the page on screen while the next window loads: the pager must not
    // unmount under the button a reader just pressed, which would drop their
    // keyboard focus and bounce the layout.
    //
    // Only within ONE list, though. An org switch or a new hash search is a
    // different list, and the previous one's rows must not sit under it for
    // even a moment — least of all another tenant's.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === orgId && prevQuery.queryKey[2] === (fileHash ?? null)
        ? prev
        : undefined,
    enabled: !!orgId,
    refetchInterval: 15_000,
  });

  const total = subsQ.data?.total ?? 0;

  // Deletions and expiries shrink the list under the reader and can leave
  // them past its end; land them on the last page that still exists rather
  // than on a table that is permanently empty.
  useEffect(() => {
    const page = subsQ.data;
    if (!page) return;
    const last = Math.floor(Math.max(0, page.total - 1) / perPage) * perPage;
    if (offset > last) setPaging({ scope, offset: last });
  }, [subsQ.data, perPage, offset, scope]);

  // Retrying after a failure must read as "retrying", not as the old failure:
  // TanStack keeps status 'error' during the refetch, so isLoading is false and
  // a stale message would otherwise sit there looking current.
  const subsLoading = subsQ.isFetching && subsQ.data === undefined;

  // Activity strip source (fair-share §5 option B): server-side counts from
  // the org's own task rows - the paginated list above undercounts once the
  // active set extends past its newest page.
  const quotaQ = useQuery({
    queryKey: ['quota', orgId],
    queryFn: () => api.getQuota(orgId!),
    enabled: !!orgId && !isAllOrgs,
    refetchInterval: 30_000,
  });

  useSSE(
    orgId && !isAllOrgs ? `/api/sse/customer?org_id=${orgId}` : null,
    () => {
      qc.invalidateQueries({ queryKey: ['submissions', orgId] });
      qc.invalidateQueries({ queryKey: ['quota', orgId] });
    },
    TASK_STREAM_KINDS,
  );

  const subs = subsQ.data?.submissions ?? [];
  const filtered = useMemo(() => {
    // Hash search is resolved server-side; show its results verbatim rather
    // than re-filtering by the hash text (which no display field contains).
    if (fileHash) return subs;
    const q = search.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter(
      (s) =>
        s.target_name.toLowerCase().includes(q) ||
        s.id.includes(q) ||
        String(s.malbox_task_id).includes(q) ||
        s.platform.includes(q) ||
        // The lane and the analyzer that served it. There is no facet bar on
        // this page - status and verdict have no filter either - so the search
        // box is where "show me the URL runs" has to be answerable, and both
        // words are ones the row already displays.
        s.target_kind.includes(q) ||
        s.analyzer?.toLowerCase().includes(q) ||
        // How the run arrived, for "show me the MCP runs": the stored value is
        // the word the lane cell shows, lowercased.
        s.intake_channel?.includes(q) ||
        (isAllOrgs && s.org_name?.toLowerCase().includes(q))
    );
  }, [subs, search, isAllOrgs, fileHash]);

  const prevStatusRef = useRef<Map<string, string>>(new Map());
  const lastOrgRef = useRef<string | null>(null);
  const [flashIds, setFlashIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!subsQ.data) return;
    if (lastOrgRef.current !== orgId) {
      prevStatusRef.current = new Map();
      lastOrgRef.current = orgId;
    }
    const prev = prevStatusRef.current;
    const flash = new Set<string>();
    for (const s of subsQ.data.submissions) {
      if (s.status == null) continue;
      const before = prev.get(s.id);
      if (before && before !== s.status) flash.add(s.id);
      prev.set(s.id, s.status);
    }
    if (flash.size) {
      setFlashIds(flash);
      const t = window.setTimeout(() => setFlashIds(new Set()), 1600);
      return () => window.clearTimeout(t);
    }
  }, [subsQ.data, orgId]);

  const newSubmissionId = (loc.state as { newSubmissionId?: string } | null)?.newSubmissionId ?? null;
  useEffect(() => {
    if (!newSubmissionId) return;
    setFlashIds((prev) => {
      const next = new Set(prev);
      next.add(newSubmissionId);
      return next;
    });
    const t = window.setTimeout(() => {
      setFlashIds((prev) => {
        const next = new Set(prev);
        next.delete(newSubmissionId);
        return next;
      });
    }, 1600);
    nav(loc.pathname, { replace: true });
    return () => window.clearTimeout(t);
  }, [newSubmissionId, nav, loc.pathname]);

  // A query handed off from the top-nav search box (AppLayout) arrives as
  // router state; apply it once and strip it so a reload doesn't re-apply.
  // The useState initializer already seeds it on a fresh mount; this covers
  // the case where Submissions is already the active route.
  const incomingQuery = (loc.state as { q?: string } | null)?.q ?? null;
  useEffect(() => {
    if (incomingQuery == null) return;
    setSearch(incomingQuery);
    nav(loc.pathname, { replace: true });
  }, [incomingQuery, nav, loc.pathname]);

  // All-orgs fallback only; the org-scoped strip reads the server counts.
  const inFlight = subs.filter((s) => s.status != null && ACTIVE_STATUSES.has(s.status)).length;
  const running = quotaQ.data?.running ?? 0;
  const queued = quotaQ.data?.queued ?? 0;

  // What the pager reports, read off the page the DATA is rather than the one
  // being requested: while a placeholder holds the previous window on screen,
  // every number in the footer has to describe the rows a reader is looking
  // at. `total` is the server's count across every page — a page-scoped text
  // search is the one case where the count and the total disagree, so it is
  // named as such.
  const pageOffset = subsQ.data?.offset ?? 0;
  const pageLimit = subsQ.data?.limit ?? perPage;
  const pageIndex = Math.floor(pageOffset / pageLimit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / pageLimit));
  const hasMore = subsQ.data?.has_more ?? false;
  // A page fetch in flight. The rows on screen are still the previous window,
  // so a second click would step from a page the reader has not seen yet.
  const paginating = subsQ.isPlaceholderData;
  const searching = search.trim() !== '' && !fileHash;

  // md+ table shape. Lane, Organization and Size retire first as the column
  // narrows - the card view below md keeps every one of them.
  const columns: Column<Submission>[] = [
    {
      key: 'sample',
      header: 'Sample',
      cell: (s) => (
        <div className="cell-file">
          {getFileIcon(s.target_name, undefined, s.target_kind)}
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-medium wrap-anywhere">{s.target_name}</span>
              <DetonationTag kind={s.detonation_kind} lane={s.lane} />
            </div>
            <div className="cell-id text-xs">
              <CopyableId id={s.malbox_task_id} className="-ml-0" />
            </div>
            {s.matched_via && (
              <span className="text-xs font-medium" style={{ color: 'var(--accent-blue)' }}>
                {s.matched_via === 'sample' ? 'sample hash match' : 'dropped-file match'}
              </span>
            )}
          </div>
        </div>
      ),
    },
    ...(isAllOrgs
      ? [
          {
            key: 'org',
            header: 'Organization',
            hideBelow: 'xl' as const,
            cellClassName: 'text-muted text-sm',
            cell: (s: Submission) => s.org_name ?? '-',
          },
        ]
      : []),
    // The run's route, one short fact per line: where it happened, what it
    // ran on, how it arrived. Stacked rather than joined with separators
    // because the column is narrow and a joined line wrapped mid-label
    // ("Windows" over "10"). A fact the row cannot state is simply absent -
    // a guest nothing resolved, a row older than the channel column - so
    // the cell is never padded with a placeholder line.
    {
      key: 'lane',
      header: 'Lane',
      hideBelow: 'lg',
      cell: (s) => (
        <div className="flex flex-col gap-0.5 whitespace-nowrap">
          <RunnerLine s={s} />
          <GuestLine s={s} />
          <IntakeChannelTag channel={s.intake_channel} />
        </div>
      ),
    },
    { key: 'status', header: 'Status', cell: (s) => <StatusBadge status={s.status} /> },
    // Status says where the run IS; verdict says what it concluded. Both are
    // shown because a run can be complete and malicious or complete and clean,
    // and one column cannot carry the answer to two questions.
    {
      key: 'verdict',
      header: 'Verdict',
      cell: (s) => <VerdictBadge family={s.family} verdict={s.verdict} tags={s.tags} />,
    },
    {
      key: 'size',
      header: 'Size',
      hideBelow: 'xl',
      cellClassName: 'text-muted text-sm tabular-nums',
      cell: (s) => formatSubmittedSize(s.lane, s.sample_size),
    },
    // Submission to completion, queue wait included - the wall time an
    // operator actually waited. A dash while the run is open; the report page
    // owns the live clock. The run's budget rides underneath: the two time
    // facts about a run read as one cell, and the Lane cell keeps to the run's
    // route - where it happened and how it arrived.
    {
      key: 'duration',
      header: 'Duration',
      hideBelow: 'lg',
      cellClassName: 'text-muted text-sm tabular-nums whitespace-nowrap',
      cell: (s) => {
        const timeout = formatTimeoutShort(s.timeout_seconds ?? 0);
        return (
          <div>
            <div>{formatWallDuration(s.submitted_at, s.completed_on)}</div>
            {timeout && <div className="text-xs">{timeout} timeout</div>}
          </div>
        );
      },
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cellClassName: 'cell-time text-sm tabular-nums whitespace-nowrap',
      cell: (s) => formatRelativeTime(s.submitted_at),
    },
    {
      key: 'go',
      header: '',
      align: 'right',
      cellClassName: 'cell-action',
      cell: () => <ArrowRight size={16} className="inline text-muted" aria-hidden />,
    },
  ];

  return (
    <div className="max-w-[1200px] mx-auto">
      <div className="page-header">
        <div>
          <h1>Submissions</h1>
          <p className="text-muted">
            {isAllOrgs ? 'Submissions across all organizations.' : 'Every analysis your organization has submitted.'}
          </p>
        </div>
        <OrgPicker />
      </div>

      {/* Search takes the row on a phone and shares it once there is room. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="search-box flex-1 sm:flex-none sm:w-[360px] sm:max-w-none">
          <Search size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by filename, task ID, or SHA-256"
            aria-label="Search submissions"
          />
        </div>

        {/* Activity beside the search box. The org-scoped numbers are the
            server's own task counts; the all-orgs view has only the rows it
            is showing, which is what an admin sees anyway. */}
        {subs.length > 0 && (isAllOrgs ? inFlight > 0 : running + queued > 0) && (
          <span
            className="text-muted text-sm inline-flex items-center gap-1.5 px-2 py-0.5 rounded-xl font-medium"
            style={{ color: 'var(--accent-blue)', background: 'var(--status-info-bg)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {isAllOrgs ? `${inFlight} in flight` : `${running} running · ${queued} queued`}
          </span>
        )}
      </div>

      {subsQ.isError ? (
        <ErrorState
          title="Couldn't load submissions"
          description="The orchestrator didn't respond. Try again."
          retry={() => subsQ.refetch()}
        />
      ) : (
        <ResponsiveTable<Submission>
          columns={columns}
          rows={filtered}
          rowKey={(s) => s.id}
          loading={subsLoading}
          skeletonRows={6}
          rowHref={(s) => `/submissions/${s.id}`}
          rowClassName={(s) => (flashIds.has(s.id) ? 'animate-row-flash' : undefined)}
          onRowHover={(s) =>
            qc.prefetchQuery({
              queryKey: ['submission', s.id],
              queryFn: () => api.getSubmission(s.id),
              staleTime: 30_000,
            })
          }
          emptyState={
            // Three different facts, in the order they can be true: a search
            // that matched nothing, a page past the end of a list that shrank
            // under the reader, and an org with nothing submitted yet.
            search ? (
              <EmptyState
                icon={Search}
                title="No matches"
                description={
                  searching
                    ? `Nothing on this page matches "${search}".`
                    : `Nothing matches "${search}".`
                }
              />
            ) : offset > 0 ? (
              <EmptyState
                icon={Inbox}
                title="Nothing on this page"
                description="This page is past the end of the list."
                action={
                  <button
                    className="btn-secondary"
                    onClick={() => setPaging({ scope, offset: 0 })}
                    type="button"
                  >
                    Go to the newest page
                  </button>
                }
              />
            ) : (
              <EmptyState
                icon={Inbox}
                title="No submissions yet"
                description="Submit a sample to see results here."
                action={
                  <Link to="/submit" className="btn-primary">
                    <Upload size={16} /> Submit your first sample
                  </Link>
                }
              />
            )
          }
          renderCard={(s) => (
            <div className="flex items-start gap-3">
              <span className="shrink-0 mt-0.5">{getFileIcon(s.target_name, undefined, s.target_kind)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium wrap-anywhere">{s.target_name}</span>
                  <DetonationTag kind={s.detonation_kind} lane={s.lane} />
                </div>
                <div className="cell-id text-xs mt-0.5">
                  <CopyableId id={s.malbox_task_id} className="-ml-0" />
                </div>
                {s.matched_via && (
                  <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--accent-blue)' }}>
                    {s.matched_via === 'sample' ? 'sample hash match' : 'dropped-file match'}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                  <StatusBadge status={s.status} />
                  <VerdictBadge family={s.family} verdict={s.verdict} tags={s.tags} />
                  <RunnerLine s={s} />
                  <GuestLine s={s} />
                  <IntakeChannelTag channel={s.intake_channel} />
                  {isAllOrgs && <span className="text-muted text-xs">{s.org_name ?? '-'}</span>}
                  <span className="text-muted text-xs tabular-nums">{formatSubmittedSize(s.lane, s.sample_size)}</span>
                  {s.completed_on && (
                    <span className="text-muted text-xs tabular-nums">
                      {formatWallDuration(s.submitted_at, s.completed_on)}
                    </span>
                  )}
                  {(s.timeout_seconds ?? 0) > 0 && (
                    <span className="text-muted text-xs tabular-nums">
                      {formatTimeoutShort(s.timeout_seconds ?? 0)} timeout
                    </span>
                  )}
                  <span className="text-muted text-xs tabular-nums">
                    {formatRelativeTime(s.submitted_at)}
                  </span>
                </div>
              </div>
            </div>
          )}
        />
      )}

      {/* The pager, mounted for as long as the list has rows to page through —
          including while the next window loads, so the control a reader just
          pressed stays where their focus is. The count is the server's total
          across every page, so it stays right on any page; a text search
          filters the rows this page already holds, so it says so rather than
          implying it saw the rest. */}
      {!subsQ.isError && total > 0 && (
        <nav
          aria-label="Submissions pagination"
          className="mt-3 flex flex-wrap items-center justify-between gap-3"
        >
          <p className="text-muted text-sm tabular-nums">
            {searching
              ? `${filtered.length} of ${subs.length} on this page`
              : subs.length > 0
                ? `Showing ${pageOffset + 1}–${pageOffset + subs.length} of ${total}`
                : `${total} in total`}
          </p>
          <div className="flex items-center gap-2">
            <Select
              ariaLabel="Rows per page"
              onChange={(value) => setPerPage(Number(value))}
              options={PER_PAGE_OPTIONS}
              size="sm"
              value={String(perPage)}
            />
            {/* aria-disabled rather than disabled, like every control that
                starts work here: disabling the button the reader just pressed
                would hand focus to <body> mid-page-turn. The checks in the
                handlers, not the attribute, are what make it a no-op. */}
            <button
              aria-busy={paginating}
              aria-disabled={paginating || pageOffset === 0}
              className="btn-secondary btn-sm"
              onClick={() => {
                if (paginating || pageOffset === 0) return;
                setPaging({ scope, offset: pageOffset - pageLimit });
              }}
              type="button"
            >
              <ChevronLeft className="w-3.5 h-3.5" aria-hidden />
              Previous
            </button>
            <span className="text-muted text-sm tabular-nums">
              Page {pageIndex} of {pageCount}
            </span>
            <button
              aria-busy={paginating}
              aria-disabled={paginating || !hasMore}
              className="btn-secondary btn-sm"
              onClick={() => {
                if (paginating || !hasMore) return;
                setPaging({ scope, offset: pageOffset + pageLimit });
              }}
              type="button"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" aria-hidden />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}

// Where the run happened: a guest on a virtual machine or on bare metal, or an
// analysis container. A container run is pinned to no guest, so `os_family`
// is null, and neither URL intake path sets `bare_metal`, so it is always
// false - the row used to read "VM" for a run that touched no virtual
// machine. The run budget is NOT here: it rides under Duration, where the two
// time facts about a run read as one cell.
function RunnerLine({ s }: { s: Submission }) {
  if (isContainerLane(s.lane)) {
    return (
      <span className="text-muted text-xs inline-flex items-center gap-1.5">
        <Box size={14} />
        Container
      </span>
    );
  }
  return (
    <span className="text-muted text-xs inline-flex items-center gap-1.5">
      {s.bare_metal ? (
        <HardDrive size={14} style={{ color: 'var(--status-warning)' }} />
      ) : (
        <Server size={14} />
      )}
      {s.bare_metal ? 'Bare metal' : 'VM'}
    </span>
  );
}

// What the run ran on. For a detonation that is the guest family it was
// pinned to, with its brand glyph: the lane alone said neither which Windows
// a run was pinned to nor that the deployment has more than one, and that is
// the first thing an analyst asks of a result they cannot reproduce. For a
// container run it is the analyzer that served it, which answers the same
// question. Absent only when the submission resolved neither.
function GuestLine({ s }: { s: Submission }) {
  if (isContainerLane(s.lane)) {
    if (!s.analyzer) return null;
    return <span className="text-muted text-xs truncate">{s.analyzer}</span>;
  }
  if (!s.os_family) return null;
  return (
    <span className="text-muted text-xs inline-flex items-center gap-1.5">
      {/* Compact glyph only - the visible text beside it carries the name,
          so the icon is decorative and hidden from readers. */}
      {hasOSGlyph(s.os_family) && (
        <span aria-hidden="true">
          <OSIcon family={s.os_family} compact />
        </span>
      )}
      {osFamilyLabel(s.os_family)}
    </span>
  );
}
